import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { all_routes } from '../router/all_routes'
import ImageWithBasePath from '../../core/common/imageWithBasePath';
import Table from "../../core/common/dataTable/index";
import PredefinedDateRanges from '../../core/common/datePicker';
import { payment_details } from '../../core/data/json/payments_details';
import CollapseHeader from '../../core/common/collapse-header/collapse-header';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAllExpenses } from '../../core/data/redux/payroll/Accounting/expenses/expenseActions';
import { AppDispatch } from '../../core/data/redux/store';
import { filterData } from '../../core/common/GlobalFunctions/tableFilters';
import { exportToCSV, exportToExcel, exportToPDF } from '../../core/common/GlobalFunctions/exportTableData';
import { useAppConfig } from '../../customhooks/useAppConfig';
import { formatDate } from '../../core/utils/configFormatters';
import { decryptData } from '../../core/utils/encryptUtils';

const Payments = () => {
    const { records } = useSelector((state: any) => state.accounting.expenses);
    const dispatch = useDispatch<AppDispatch>();
    const [selectedStatus, setSelectedStatus] = useState('All');
    const [selectedSort, setSelectedSort] = useState<any>("");
    const [tableKey, setTableKey] = useState(0);

    useEffect(() => {
        dispatch(fetchAllExpenses())
    }, [dispatch])

    const filteredData = useMemo(() => {
        const filters =
            selectedStatus && selectedStatus !== "All"
                ? { paymentStatus: selectedStatus }
                : {};

        return filterData(records, filters, "createdAt", "createdAt", selectedSort);
    }, [records, selectedStatus, selectedSort]);

    const exportData = filteredData.map((item: any) => ({
        expense_id: item.expense_id || "N/A",
        expense_name: item.expense_name || "N/A",
        expense_date: item.expense_date ? new Date(item.expense_date).toLocaleDateString("en-IN") : "N/A",
        amount: item.amount ?? 0,
        payment_method: item.payment_method || "N/A",
        status: item.status || "Inactive",
        createdBy: item.createdBy || "N/A",
        modifiedBy: item.modifiedBy || "N/A",
        createdAt: item.createdAt ? new Date(item.createdAt).toLocaleDateString("en-IN") : "N/A",
        modifiedAt: item.modifiedAt ? new Date(item.modifiedAt).toLocaleDateString("en-IN") : "N/A",
        requestStatus: item.requestStatus || "Pending",
        paymentStatus: item.paymentStatus || "Unpaid",
    }));

    // Permission
    const encryptedPermissions = localStorage.getItem('currentPermissions');
    const permissions: string[] = Array.isArray(decryptData(encryptedPermissions || '')) ? decryptData(encryptedPermissions || '') : [];

    const canExport =
        permissions.includes("Export") || permissions.includes("AllowAll");

    useEffect(() => {
        setTableKey((prevKey: any) => prevKey + 1);
    }, [selectedStatus, selectedSort, records]);

    const config = useAppConfig();
    if (!config) return null;

    const columns = [
        {
            title: "Expense Name",
            dataIndex: "expense_name",
            sorter: (a: any, b: any) => a.expense_name.length - b.expense_name.length,
        },
        {
            title: "Date",
            dataIndex: "expense_date",
            render: (text: string) => formatDate(text, config),
            sorter: (a: any, b: any) => a.expense_date.length - b.expense_date.length,
        },
        {
            title: "Payment Method",
            dataIndex: "payment_method",
            sorter: (a: any, b: any) => a.payment_method.length - b.payment_method.length,
        },
        {
            title: "Amount",
            dataIndex: "amount",
            sorter: (a: any, b: any) => a.amount.length - b.amount.length,
        },
        {
            title: "Payment Status ",
            dataIndex: "paymentStatus",
            render: (text: string, record: any) => (
                <span className={`badge ${text === 'Paid' ? 'badge-success' : 'badge-danger'} d-inline-flex align-items-center badge-xs`}>
                    <i className="ti ti-point-filled me-1" />
                    {text}
                </span>

            ),
        },
    ]

    return (
        <>
            {/* Page Wrapper */}
            <div className="page-wrapper">
                <div className="content">
                    {/* Breadcrumb */}
                    <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
                        <div className="my-auto mb-2">
                            <h2 className="mb-1">Payments</h2>
                            <nav>
                                <ol className="breadcrumb mb-0">
                                    <li className="breadcrumb-item">
                                        <Link to={all_routes.adminDashboard}>
                                            <i className="ti ti-smart-home" />
                                        </Link>
                                    </li>
                                    <li className="breadcrumb-item">HR</li>
                                    <li className="breadcrumb-item active" aria-current="page">
                                        Payments
                                    </li>
                                </ol>
                            </nav>
                        </div>
                        <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
                            {canExport && (
                                <div className="mb-2">
                                    <div className="dropdown">
                                        <Link
                                            to="#"
                                            className="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                                            data-bs-toggle="dropdown"
                                        >
                                            <i className="ti ti-file-export me-1" />
                                            Export
                                        </Link>
                                        <ul className="dropdown-menu  dropdown-menu-end p-3">
                                            <li>
                                                <button
                                                    onClick={() => exportToCSV(exportData, 'payments_data')}
                                                    className="dropdown-item rounded-1"
                                                >
                                                    <i className="ti ti-file-type-pdf me-1" />
                                                    Export as CSV
                                                </button>
                                            </li>
                                            <li>
                                                <button
                                                    onClick={() => exportToExcel(exportData, 'payments_data')}
                                                    className="dropdown-item rounded-1"
                                                >
                                                    <i className="ti ti-file-type-xls me-1" />
                                                    Export as Excel{" "}
                                                </button>
                                            </li>
                                            <li>
                                                <button
                                                    onClick={() => exportToPDF(exportData, 'payments_data')}
                                                    className="dropdown-item rounded-1"
                                                >
                                                    <i className="ti ti-file-type-xls me-1" />
                                                    Export as PDF{" "}
                                                </button>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            )}
                            <div className="head-icons ms-2">
                                <CollapseHeader />
                            </div>
                        </div>
                    </div>
                    {/* /Breadcrumb */}
                    <div className="card">
                        <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                            <h5>Payment List</h5>
                            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                                <div className="dropdown me-3">
                                    <Link
                                        to="#"
                                        className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center"
                                        data-bs-toggle="dropdown"
                                    >
                                        {selectedStatus ? selectedStatus.charAt(0).toUpperCase() + selectedStatus.slice(1) : "Select Status"}
                                    </Link>
                                    <ul className="dropdown-menu  dropdown-menu-end p-3">
                                        <li>
                                            <Link
                                                to="#"
                                                onClick={() => setSelectedStatus('All')}
                                                className="dropdown-item rounded-1"
                                            >
                                                All
                                            </Link>
                                        </li>
                                        <li>
                                            <Link
                                                to="#"
                                                onClick={() => setSelectedStatus('Paid')}
                                                className="dropdown-item rounded-1"
                                            >
                                                Paid
                                            </Link>
                                        </li>
                                        <li>
                                            <Link
                                                to="#"
                                                onClick={() => setSelectedStatus('Unpaid')}
                                                className="dropdown-item rounded-1"
                                            >
                                                Unpaid
                                            </Link>
                                        </li>
                                    </ul>
                                </div>
                                <div className="dropdown">
                                    <Link
                                        to="#"
                                        className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center"
                                        data-bs-toggle="dropdown"
                                    >
                                        {selectedSort ? `Sort By : ${selectedSort}` : "Sort By : Last 7 Days"}
                                    </Link>
                                    <ul className="dropdown-menu dropdown-menu-end p-3">
                                        <li>
                                            <Link to="#" onClick={() => setSelectedSort("Recently Added")} className="dropdown-item rounded-1">
                                                Recently Added
                                            </Link>
                                        </li>
                                        <li>
                                            <Link to="#" onClick={() => setSelectedSort("Ascending")} className="dropdown-item rounded-1">
                                                Ascending
                                            </Link>
                                        </li>
                                        <li>
                                            <Link to="#" onClick={() => setSelectedSort("Descending")} className="dropdown-item rounded-1">
                                                Descending
                                            </Link>
                                        </li>
                                        <li>
                                            <Link to="#" onClick={() => setSelectedSort("Last Month")} className="dropdown-item rounded-1">
                                                Last Month
                                            </Link>
                                        </li>
                                        <li>
                                            <Link to="#" onClick={() => setSelectedSort("Last 7 Days")} className="dropdown-item rounded-1">
                                                Last 7 Days
                                            </Link>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <div className="card-body p-0">
                            <Table key={tableKey} dataSource={filteredData} columns={columns} Selection={false} />
                        </div>
                    </div>
                </div>
                <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
                    <p className="mb-0">2024 - 2025© hrmtree.</p>
                    <p>
                        Developed By{" "}
                        <Link to="#" className="text-primary">
                            Leadchain Systems
                        </Link>
                    </p>
                </div>
            </div>
            {/* /Page Wrapper */}
        </>



    )
}

export default Payments
